'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { LiveAuctionCard, UpcomingAuctionCard } from '@/components/auction-cards';
import FilterDropdown from '@/components/FilterDropdown';
import PriceRangeFilter from '@/components/PriceRangeFilter';
import Pagination from '@/components/Pagination';
import { Auction, City, District, AuctionListRequest } from '@/lib/types/api';
import { apiService } from '@/lib/api-client';
import { debugAuctionDates, debugTimezone } from '@/lib/utils';
import { useFavorites } from '@/lib/hooks/useFavorites';

interface AuctionsPageClientProps {
  initialAuctions: Auction[];
  cities: City[];
  initialPaginationInfo?: {
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    currentPage: number;
  };
  initialFilters?: {
    propertyTypes: string[];
    cities: string[];
    districts: string[];
    priceRanges: string[];
  };
  isAuthenticated?: boolean; // Server-side'dan gelecek
}

export default function AuctionsPageClient({
  initialAuctions,
  cities,
  initialPaginationInfo,
  initialFilters = {
    propertyTypes: [],
    cities: [],
    districts: [],
    priceRanges: []
  },
  isAuthenticated = false
}: AuctionsPageClientProps) {
  const t = useTranslations('auctions');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  
  const [auctions, setAuctions] = useState<Auction[]>(initialAuctions);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPaginationInfo?.currentPage || 1);
  const [totalPages, setTotalPages] = useState(initialPaginationInfo?.totalPages || 1);
  const [totalCount, setTotalCount] = useState(initialPaginationInfo?.totalCount || 0);


  
  // Backend RealEstateType enum'ına göre gayrimenkul türleri
  const propertyTypes = [
    { value: '3', label: t('propertyTypes.devrenSatilik') },
    { value: '4', label: t('propertyTypes.devrenKiralik') },
    { value: '2', label: t('propertyTypes.land') },
    { value: '1', label: t('propertyTypes.residential') },
    { value: '5', label: t('propertyTypes.isyeri') },
    { value: '6', label: t('propertyTypes.turizmTesisi') },
    { value: '7', label: t('propertyTypes.konutProjesi') },
    { value: '8', label: t('propertyTypes.tarla') }
  ];

  // API'den gelen şehirleri FilterDropdown için uygun formata dönüştür
  const cityOptions = cities.map(city => ({
    value: city.id, // Şehir ID'sini value olarak kullan
    label: city.name || 'İsimsiz Şehir'
  }));

  const [districts, setDistricts] = useState<District[]>([]);

  // API'den gelen ilçeleri FilterDropdown için uygun formata dönüştür
  const districtOptions = districts.map(district => ({
    value: district.id, // İlçe ID'sini value olarak kullan
    label: district.name || 'İsimsiz İlçe'
  }));
  
  // Filtreleri initialFilters'den başlat (server-side'dan gelen URL parametrelerinden)
  const [filters, setFilters] = useState({
    propertyTypes: initialFilters.propertyTypes || [],
    cities: initialFilters.cities || [],
    districts: initialFilters.districts || [],
    priceRanges: initialFilters.priceRanges || []
  });

  const [favorites, setFavorites] = useState<string[]>([]);
  const { toggleFavorite } = useFavorites();

  // URL'i filtrelerle ve sayfa numarasıyla güncelle
  const updateURL = useCallback((newFilters: typeof filters, pageNum?: number) => {
    const params = new URLSearchParams();
    
    // Filtreleri URL parametrelerine ekle
    if (newFilters.propertyTypes.length > 0) {
      newFilters.propertyTypes.forEach(tip => {
        params.append('tip', tip);
      });
    }
    
    // Şehir tekli seçim olduğu için sadece ilk değeri kullan
    if (newFilters.cities.length > 0) {
      params.set('sehir', newFilters.cities[0]);
    }
    
    if (newFilters.districts.length > 0) {
      newFilters.districts.forEach(ilce => {
        params.append('ilce', ilce);
      });
    }
    
    // Fiyat aralığı filtresini parse et ve URL'e ekle
    if (newFilters.priceRanges.length > 0) {
      const rangeId = newFilters.priceRanges[0];
      if (rangeId.startsWith('manual-')) {
        const [min, max] = rangeId.replace('manual-', '').split('-').map(Number);
        if (min && !isNaN(min) && min > 0) {
          params.set('minFiyat', min.toString());
        }
        if (max && !isNaN(max) && max < 999999999) {
          params.set('maxFiyat', max.toString());
        }
      }
    }
    
    // Sayfa numarasını ekle (1'den büyükse)
    if (pageNum !== undefined && pageNum > 1) {
      params.set('sayfa', pageNum.toString());
    }
    
    // URL'i güncelle (scroll: false ile sayfa kaydırmadan)
    const newURL = params.toString() ? `${pathname}?${params.toString()}` : pathname;
    router.replace(newURL, { scroll: false });
  }, [pathname, router]);

  // İlk yüklemede URL'den ilçeleri yükle (eğer şehir seçiliyse)
  useEffect(() => {
    if (filters.cities.length > 0 && districts.length === 0) {
      // Sadece ilçe listesini yükle, filtreleri değiştirme
      apiService.districts.getByCityIds(filters.cities).then(response => {
        if (response.isSuccess && response.data) {
          setDistricts(response.data);
        }
      }).catch(error => {
        // Error loading districts
      });
    }
  }, [filters.cities, districts.length]);

  const handleFilterChange = async (filterType: string, value: string[] | string) => {
    // Yeni filtreleri hesapla
    const newFilters = {
      ...filters,
      [filterType]: value
    };

    // Şehir seçildiğinde ilçeleri getir
    if (filterType === 'cities') {
      const selectedCities = Array.isArray(value) ? value : [value];
      
      if (selectedCities.length > 0) {
        try {
          // Birden fazla şehir seçildiğinde yeni API'yi kullan
          const response = await apiService.districts.getByCityIds(selectedCities);
          if (response.isSuccess && response.data) {
            setDistricts(response.data);
          } else {
            setDistricts([]);
          }
        } catch (error) {
          setDistricts([]);
        }
      } else {
        setDistricts([]);
        newFilters.districts = [];
      }
    }

    // State'i güncelle
    setFilters(newFilters);
    setCurrentPage(1);

    // Filtre değiştiğinde sayfa 1'e dön ve yeni veri çek
    await fetchAuctionsWithFilters(1, true, newFilters);
    
    // URL'i güncelle (sayfa 1 ile)
    updateURL(newFilters, 1);
  };

  // Filtrelerle API çağrısı
  const fetchAuctionsWithFilters = useCallback(async (page: number = 1, resetFilters: boolean = false, customFilters?: typeof filters) => {
    const filtersToUse = customFilters || filters;
    

    
    setLoading(true);
    setError(null);
    
    try {
      // Fiyat aralığı filtresini parse et
      let minPrice: number | null = null;
      let maxPrice: number | null = null;
      
      if (filtersToUse.priceRanges.length > 0) {
        const rangeId = filtersToUse.priceRanges[0];
        if (rangeId.startsWith('manual-')) {
          const [min, max] = rangeId.replace('manual-', '').split('-').map(Number);
          minPrice = min;
          maxPrice = max;
        }
      }

      const requestParams: AuctionListRequest = {
        pageNumber: page,
        pageSize: 20,
        status: [1, 2], // Aktif ve yaklaşan müzayedeler
        realEstateType: filtersToUse.propertyTypes.length > 0 ? filtersToUse.propertyTypes.map(type => parseInt(type)) : [],
        cityId: filtersToUse.cities.length > 0 ? filtersToUse.cities : [],
        districtId: filtersToUse.districts.length > 0 ? filtersToUse.districts : [],
        minPrice,
        maxPrice
      };



      const response = await apiService.auctions.getList(requestParams);
      

      
      if (response.isSuccess && response.data) {
        // Sayfa değiştiğinde veya filtre değiştiğinde listeyi replace et
        setAuctions(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
        // pageIndex 0'dan başladığı için +1 ekliyoruz
        const newCurrentPage = response.data.pageIndex + 1;
        setCurrentPage(newCurrentPage);
        
        // URL'i güncelle (sadece sayfa değiştiyse ve resetFilters false ise)
        if (!resetFilters) {
          updateURL(filtersToUse, newCurrentPage);
        }
        

        
        // Debug: Auction tarihlerini kontrol et
        if (response.data?.data && response.data.data.length > 0) {
          response.data.data.forEach(auction => {
            debugAuctionDates(auction);
            // İlk auction'ın timezone'ını da debug et
            if (response.data!.data.indexOf(auction) === 0) {
              debugTimezone(auction.startDate);
              debugTimezone(auction.endDate);
            }
          });
        }
      } else {
        setError(response.message || 'Müzayede verileri yüklenirken bir hata oluştu');
      }
    } catch (err) {
      setError('Müzayede verileri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [filters, updateURL]);

  const handleFavoriteToggle = async (id: string) => {
    // Mevcut favori durumunu bul
    const auction = auctions.find(a => a.id === id);
    const currentFavoriteStatus = auction?.isFavorite || false;
    
    try {
      // API'ye istek gönder
      await toggleFavorite(id, currentFavoriteStatus);
      
      // Client favori ID listesinde toggle
      setFavorites(prev => 
        currentFavoriteStatus
          ? prev.filter(favId => favId !== id)
          : [...prev, id]
      );

      // Kart veri kaynağında (auctions) isFavorite alanını eşzamanlı güncelle
      setAuctions(prev => prev.map(a => a.id === id ? { ...a, isFavorite: !a.isFavorite } : a));
    } catch (error) {
      // Error toggling favorite
    }
  };

  // API'den gelen isFavorite bilgisine göre favorileri seed et ve
  // yeni liste verisi geldiğinde client state ile birleştir
  useEffect(() => {
    const apiFavIds = auctions.filter(a => a.isFavorite).map(a => a.id);
    if (apiFavIds.length === 0) return;
    setFavorites(prev => {
      const merged = new Set<string>([...prev, ...apiFavIds]);
      return Array.from(merged);
    });
  }, [auctions]);

  const clearFilters = async () => {
    const clearedFilters = {
      propertyTypes: [],
      cities: [],
      districts: [],
      priceRanges: []
    };
    setFilters(clearedFilters);
    setDistricts([]);
    setCurrentPage(1);
    
    await fetchAuctionsWithFilters(1, true, clearedFilters);
    
    // URL'i temizle (sadece pathname'e dön)
    router.replace(pathname, { scroll: false });
  };

  // Sayfa değişikliği handler
  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
      fetchAuctionsWithFilters(page, false);
      // Sayfa değiştiğinde scroll'u en üste al
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, totalPages, loading, fetchAuctionsWithFilters]);

  // API verilerini kart komponentlerine uygun formata dönüştür
  const transformAuctionForCard = (auction: Auction) => ({
    id: auction.id,
    slug: auction.slug,
    title: auction.title,
    location: `${auction.districtName}, ${auction.cityName}`,
    startingPrice: auction.startingPrice,
    image: auction.image || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    // Görünüm: API isFavorite ya da client state true ise favori göster
    isFavorite: isAuthenticated 
      ? (favorites.includes(auction.id) || auction.isFavorite === true)
      : auction.isFavorite === true,
    onFavoriteToggle: isAuthenticated ? handleFavoriteToggle : undefined
  });

  // Status'a göre müzayede türünü belirle
  const getAuctionType = (auction: Auction) => {
    return auction.status === 2 ? 'live' : 'upcoming'; // Status 2: devam eden (endDate), Status 1: yaklaşan (startDate)
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 pt-16 sm:pt-20 lg:pt-24">
        <div className="w-[95%] sm:w-[90%] mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('pageTitle')}
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mb-4 sm:mb-6 px-2">
              {t('pageSubtitle')}
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
            <FilterDropdown
              label={t('filters.type')}
              options={propertyTypes}
              selectedValues={filters.propertyTypes}
              onSelect={(values) => handleFilterChange('propertyTypes', values)}
              searchable={false}
              className="w-full sm:w-auto"
            />
            <FilterDropdown
              label={t('filters.city')}
              options={cityOptions}
              selectedValues={filters.cities}
              onSelect={(values) => handleFilterChange('cities', values)}
              searchable={true}
              singleSelect={true}
              className="w-full sm:w-auto"
            />
            <FilterDropdown
              label={t('filters.district')}
              options={districtOptions}
              selectedValues={filters.districts}
              onSelect={(values) => handleFilterChange('districts', values)}
              searchable={true}
              disabled={filters.cities.length === 0 || districtOptions.length === 0}
              className="w-full sm:w-auto"
            />
            <PriceRangeFilter
              selectedRanges={filters.priceRanges}
              onRangesChange={(ranges) => handleFilterChange('priceRanges', ranges)}
              className="w-full sm:w-auto"
            />
            <button 
              onClick={() => clearFilters()}
              className="px-3 sm:px-4 py-2 text-gray-500 hover:text-gray-700 font-medium transition-colors text-sm sm:text-base underline hover:no-underline whitespace-nowrap"
            >
              {t('filters.clearFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-[95%] sm:w-[90%] mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Hata mesajı */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-red-800 text-sm sm:text-base">{error}</p>
          </div>
        )}

        {/* Müzayede Listesi */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 gap-y-6 sm:gap-y-9 auto-rows-fr">
          {auctions.map((auction, index) => {
            const cardData = transformAuctionForCard(auction);
            const auctionType = getAuctionType(auction);
            // İlk sayfadaki ilk 4 görsel için priority kullan (LCP optimizasyonu)
            const isPriority = currentPage === 1 && index < 4;
            
            if (auctionType === 'live') {
              // Status 2: Devam eden müzayede - endDate ile kalan süre hesaplanır
              return (
                <LiveAuctionCard
                  key={auction.id}
                  {...cardData}
                  endDate={auction.endDate}
                  isAuthenticated={isAuthenticated}
                  priority={isPriority}
                />
              );
            } else {
              // Status 1: Yaklaşan müzayede - startDate ile kalan süre hesaplanır
              return (
                <UpcomingAuctionCard
                  key={auction.id}
                  {...cardData}
                  startDate={auction.startDate}
                  isAuthenticated={isAuthenticated}
                  priority={isPriority}
                />
              );
            }
          })}
        </div>



        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 sm:mt-12 pb-6 sm:pb-8">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={20}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
              <span className="text-gray-600 text-sm">Yükleniyor...</span>
            </div>
          </div>
        )}

        {/* Boş durum */}
        {auctions.length === 0 && !loading && !error && (
          <div className="text-center py-8 sm:py-12 px-4">
            <div className="text-gray-400 text-4xl sm:text-6xl mb-3 sm:mb-4">🏠</div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              {t('noAuctionsFound')}
            </h3>
            <p className="text-gray-600 text-sm sm:text-base">
              {t('noAuctionsDescription')}
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
